/**
 * Tempo AND downbeat, tapped against a clip that has no click.
 *
 * "Extract melody" reads a recording nobody performed to a grid, so v1 asks the user for the two
 * numbers automatic estimation would have to guess (§5; estimation is v2). The tempo is a median of
 * the intervals, for the same reason the roll's tap tempo uses one: one late tap should not drag the
 * result the way it would in an average. The downbeat is the FIRST tap, because the user was asked
 * to start tapping on one - a median of a single decision is that decision.
 *
 * Deliberately not `bpmFromTaps` from the roll header: that answers "what tempo is the player
 * playing" for a live count that keeps running, and it has no notion of where in a recording the
 * count started, which is exactly the second number this needs.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §5
 */

export interface TappedTempo {
  bpm: number
  /** The first tap's position in the clip, ms from the clip's start. This is beat 1. */
  downbeatMs: number
}

/**
 * Four taps: three intervals, which is the fewest a median can be taken over without simply being
 * the middle of two guesses. §5 sets this floor.
 */
export const MIN_TAPS = 4

export const tempoFromTaps = (tapsMs: number[]): TappedTempo | null => {
  if (tapsMs.length < MIN_TAPS) {
    return null
  }
  const ordered = [...tapsMs].sort((a, b) => a - b)
  const intervals = ordered
    .slice(1)
    .map((tap, index) => tap - ordered[index])
    .sort((a, b) => a - b)

  const middle = Math.floor(intervals.length / 2)
  const median = intervals.length % 2 === 1 ? intervals[middle] : (intervals[middle - 1] + intervals[middle]) / 2

  if (median <= 0) {
    return null
  }

  return {
    /** Clamped into `melodySchema`'s range, so a double-tap cannot write an invalid entity. */
    bpm: Math.min(300, Math.max(20, Math.round(60000 / median))),
    downbeatMs: ordered[0],
  }
}
